import React from 'react'
import PropTypes from 'prop-types'
import Collapsible from 'react-collapsible'
import numeral from 'numeral'

import i18n from 'Pods/Shared/I18n'

import UserSidebarRating from './UserSidebarRating'
import UserSidebarDataRows from './UserSidebarDataRows'

function UserSidebarUserData({
  userInfo,
  userRatings = null,
  isResponsive = false,
  newRoyaltyUi = false,
  locale
}) {
  function renderNumber(value, units = '') {
    if (!value) {
      return <span>-</span>
    }
    return <span>{`${value}${units}`}</span>
  }

  const rating = i18n.t('profilePage.rating', { ns: 'rails', lng: locale })
  const reputation = i18n.t('profilePage.reputation', { ns: 'rails', lng: locale })
  const responseRate = i18n.t('profilePage.responseRate', { ns: 'rails', lng: locale })
  const responseTime = i18n.t('profilePage.responseTime', { ns: 'rails', lng: locale })
  const projectsDone = i18n.t('profilePage.projectsDone', { ns: 'rails', lng: locale })
  const modelViews = i18n.t('profilePage.modelViews', { ns: 'rails', lng: locale })
  const modelLikes = i18n.t('profilePage.modelLikes', { ns: 'rails', lng: locale })
  const followers = i18n.t('profilePage.followers', { ns: 'rails', lng: locale })
  const tooltipTextResponseRate = i18n.t('profilePage.tooltipTextResponseRate', { ns: 'rails', lng: locale })
  const tooltipTextProjectsDone = i18n.t('profilePage.tooltipTextProjectsDone', { ns: 'rails', lng: locale, projectCount: userInfo.projectsActive })
  const tooltipTextResponseTime = i18n.t('profilePage.tooltipTextResponseTime', { ns: 'rails', lng: locale })
  const tooltipTextModelViews = i18n.t('profilePage.tooltipTextModelViews', { ns: 'rails', lng: locale })
  const tooltipTextModelLikes = i18n.t('profilePage.tooltipTextModelLikes', { ns: 'rails', lng: locale })
  const tooltipTextFollowers = i18n.t('profilePage.tooltipTextFollowers', { ns: 'rails', lng: locale })

  let pattern = [
    {
      icons: [
        {
          icon: 'star',
          type: 'far',
        },
      ],
      title: rating,
      data: <UserSidebarRating rating={userInfo.rating} />,
      isForSmallScreen: true,
    },
    {
      icons: [
        {
          icon: 'hashtag',
          type: 'far',
        },
      ],
      title: reputation,
      data: renderNumber(userInfo.rankDetails.reputation),
      isForSmallScreen: true,
    },
    {
      icons: [
        {
          icon: 'comment',
          type: 'far',
        },
        {
          icon: 'reply',
          type: 'fas',
        },
      ],
      title: responseRate,
      data: renderNumber(userInfo.responseRate, '%'),
      isForSmallScreen: false,
      tooltipText: tooltipTextResponseRate,
    },
    {
      icons: [
        {
          icon: 'comment',
          type: 'far',
        },
        {
          icon: 'clock',
          type: 'fas',
        },
      ],
      title: responseTime,
      data: renderNumber(userInfo.responseTime, ' h'),
      isForSmallScreen: false,
      tooltipText: tooltipTextResponseTime,
    },
    {
      icons: [
        {
          icon: 'handshake-alt',
          type: 'far',
        },
      ],
      title: projectsDone,
      data: renderNumber(userInfo.projectsDone),
      isForSmallScreen: false,
      tooltipText: tooltipTextProjectsDone,
    },
    {
      icons: [
        {
          icon: 'eye',
          type: 'far',
        },
      ],
      title: modelViews,
      data: renderNumber(
        numeral(userInfo.modelViews).format(
          userInfo.modelViews > 1000 ? '0.0a' : '0a'
        )
      ),
      isForSmallScreen: true,
      tooltipText: tooltipTextModelViews,
    },
    {
      icons: [
        {
          icon: 'heart',
          type: 'far',
        },
      ],
      title: modelLikes,
      data: renderNumber(userInfo.modelLikes),
      isForSmallScreen: false,
      tooltipText: tooltipTextModelLikes,
    },
    {
      icons: [
        {
          icon: 'user-plus',
          type: 'far',
        },
      ],
      title: followers,
      data: renderNumber(userInfo.followers),
      isForSmallScreen: false,
      tooltipText: tooltipTextFollowers,
    },
  ]

  if (newRoyaltyUi) {
    pattern = pattern.filter((item) => item.title !== 'Reputation')
  }

  return (
    <section className="user-data__section user-data__section--with-tooltips">
      {pattern.map((dataRow, index) => {
        if (isResponsive && dataRow.isForSmallScreen || !isResponsive) {
          return (
            <UserSidebarDataRows
              key={`data-row-${index}`}
              dataRow={dataRow}
              userLevel={userInfo.rankDetails.level}
              userRating={userInfo.rating}
              userRatings={userRatings}
              index={index}
            />
          )
        }
        return null
      })}
      {isResponsive && (
        <Collapsible
          className="Collapsible--transparent Collapsible--paddingless"
          openedClassName="Collapsible--transparent Collapsible--paddingless is-opened"
          overflowWhenOpen="visible"
          trigger={(
            <h2 className="heading-14 text-bold">
              {i18n.t('more', { ns: 'global' })}
            </h2>
          )}
          triggerWhenOpen={(
            <h2 className="heading-14 text-bold">
              {i18n.t('less', { ns: 'global' })}
            </h2>
          )}
          transitionTime={300}
          open={false}
        >
          {pattern.map((dataRow, index) => {
            if (!dataRow.isForSmallScreen) {
              return (
                <UserSidebarDataRows
                  key={`data-row-${index}`}
                  dataRow={dataRow}
                  userLevel={userInfo.rankDetails.level}
                  userRating={userInfo.rating}
                  userRatings={userRatings}
                  index={`collapsible-${index}`}
                />
              )
            }
            return null
          })}
        </Collapsible>
      )}
    </section>
  )
}

UserSidebarUserData.propTypes = {
  userInfo: PropTypes.shape({
    rating: PropTypes.number,
    rank: PropTypes.number,
    projectsDone: PropTypes.number,
    projectsActive: PropTypes.number,
    modelViews: PropTypes.number,
    modelLikes: PropTypes.number,
    followers: PropTypes.number,
    responseRate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    responseTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rankDetails: PropTypes.shape({
      level: PropTypes.string,
      reputation: PropTypes.number,
    }),
  }).isRequired,
  userRatings: PropTypes.object,
  isResponsive: PropTypes.bool,
  newRoyaltyUi: PropTypes.bool,
}

export default UserSidebarUserData
